import { withDefaults } from "@warp-drive/legacy/model/migration-support";
import type { WithLegacy } from "@warp-drive/legacy/model/migration-support";
import { Type } from "@warp-drive/core/types/symbols";
import { derivation } from "../derivation";
import type ProjectModel from "glesys-controlpanel/models/project";

export type SshKey = WithLegacy<{
  description: string;
  data: string;
  type: string;
  algorithm: string;
  project: ProjectModel;
  [Type]: "ssh-key";
}>;

const ALGORITHMS: Record<string, { name: string; badge: string }> = {
  rsa: { name: "RSA", badge: "rsa" },
  ed25519: { name: "Ed25519", badge: "ed" },
  ecdsa: { name: "ECDSA", badge: "ec" },
  dss: { name: "DSA", badge: "dss" },
};

function parseKeyPrefix(data: string) {
  const [prefix = ""] = data.trim().split(/\s+/);
  const [algorithm = ""] = prefix.replace(/^sk-/, "").replace(/^ssh-/, "").split(/[-@]/);
  return { algorithm, isSecurityKey: prefix.startsWith("sk-") };
}

const sshKeyType = derivation<SshKey>("ssh-key:type", (record) => {
  const { algorithm } = parseKeyPrefix(record.data);
  return ALGORITHMS[algorithm]?.badge ?? algorithm.slice(0, 2);
});

const sshKeyAlgorithm = derivation<SshKey>("ssh-key:algorithm", (record) => {
  const { algorithm, isSecurityKey } = parseKeyPrefix(record.data);
  const name = ALGORITHMS[algorithm]?.name ?? algorithm;
  return isSecurityKey ? `${name}-SK` : name;
});

const schema = withDefaults({
  type: "ssh-key",
  fields: [
    { kind: "attribute", name: "description" },
    { kind: "attribute", name: "data" },
    { kind: "derived", name: "type", type: "ssh-key:type" },
    { kind: "derived", name: "algorithm", type: "ssh-key:algorithm" },
    {
      kind: "belongsTo",
      name: "project",
      type: "project",
      options: { async: false, inverse: "sshKeys" },
    },
  ],
});

export const resource = {
  schema,
  derivations: [sshKeyType, sshKeyAlgorithm],
};
